var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var QuantileConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import './quantileColumn'; // force import for JsonSchema
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult } from './validation/toolValidationResult';
import { buildInputsMap, validateSourceWithDetail } from './validation/validateHelper';
const schema = {
    $id: 'urn:wisepipe-common-pipe:QuantileConfig',
    type: 'object',
    properties: {
        quantileColumns: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-common-pipe:QuantileColumn' },
            minItems: 1
        },
        groupByColumns: {
            type: 'array',
            items: { type: 'string' },
            uniqueItems: true
        }
    },
    required: ['quantileColumns', 'groupByColumns']
};
let QuantileConfig = QuantileConfig_1 = class QuantileConfig extends ToolConfig {
    constructor(groupByColumns, quantileColumns, customFormats) {
        super(ToolType.QUANTILE, customFormats);
        this.groupByColumns = groupByColumns;
        this.quantileColumns = quantileColumns;
        JSON.validate(this, QuantileConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return QuantileConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return QuantileConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return QuantileConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return QuantileConfig_1.outs;
    }
    static get in() {
        return this.ins[0];
    }
    static get out() {
        return this.ins[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo('Percentile', [ToolCategories.ORGANIZE], this.ins, this.outs, ['percentile', 'quantile', 'quartile'], true);
    }
    static getNewColumn(quantCol, quant) {
        if (quant.newColumn === undefined || quant.newColumn.trim().length === 0) {
            return `${quantCol.column}_${quant.quantile}`;
        }
        return quant.newColumn;
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        if (source) {
            const exp = tsb.export(this.out);
            const dependents = this.getGroupByDependents(exp, this.in, this.groupByColumns);
            const newColumnFormats = new Map();
            this.quantileColumns.forEach((quantCol) => {
                quantCol.quantiles.forEach((quant) => {
                    const srcColumn = source.find((s) => s.name === quantCol.column);
                    const curDeps = [...dependents, this.getDependent(exp, this.in, quantCol.column)];
                    const columnName = QuantileConfig_1.getNewColumn(quantCol, quant);
                    exp.makeNew(new Column(columnName, srcColumn.type), curDeps, true);
                    if (srcColumn?.customFormat) {
                        newColumnFormats.set(columnName, srcColumn?.customFormat.copyWithAutoGeneratedTrue());
                    }
                });
            });
            exp.initializeColumnFormats().setColumnFormats(newColumnFormats).setColumnFormats(this.customFormats);
        }
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        // our groupby columns can be any type, so no need for validation there
        const groupByColumns = new Set(this.groupByColumns);
        const newColumnSet = new Set();
        const quantColsArray = [];
        for (const quantCol of this.quantileColumns) {
            for (const quant of quantCol.quantiles) {
                // construct quant newColumn if it's undefined
                const quantNewColumn = QuantileConfig_1.getNewColumn(quantCol, quant);
                // check uniqueness of quant newColumns
                if (newColumnSet.has(quantNewColumn)) {
                    return ToolValidationResult.getBadMetadataResult(`Duplicate output column name: ${quantNewColumn}.`, `Output name must be unique. Please rename one of the column ${quantNewColumn}.`);
                }
                // check overlap of out, groupby+newColumns
                if (groupByColumns.has(quantNewColumn)) {
                    return ToolValidationResult.getBadMetadataResult(`Output column name '${quantNewColumn}' is already used in group by.`, `Output name must be unique. Please rename one of the column ${quantNewColumn}.`);
                }
                newColumnSet.add(quantNewColumn);
            }
            quantColsArray.push(quantCol.column);
            // check overlap of groupby+quant for input
            if (groupByColumns.has(quantCol.column)) {
                return ToolValidationResult.getBadMetadataResult(`Column '${quantCol.column}' is used in both group by and quantileColumns.`, `Please select a column that is not in group by or change the group by column ${quantCol.column}.`);
            }
        }
        // quantiles only work on numerical columns
        const inputs = buildInputsMap(quantColsArray, ['number']);
        this.addGroupByToInputs(inputs, this.groupByColumns);
        const sourceValidationResult = validateSourceWithDetail(source, inputs, []);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
};
QuantileConfig = QuantileConfig_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.QUANTILE)
], QuantileConfig);
export { QuantileConfig };
//# sourceMappingURL=quantileConfig.js.map